import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import { AppDataGrid, type AppColumn, gridExportMatrix } from '../../components/AppDataGrid';
import { FormTitle } from '../../components/FormTitle';
import { useConfirm } from '../../components/confirm';
import { useNotify } from '../../components/notifications/useNotify';
import { runLoading } from '../../components/loading';
import { AppToolbar } from '../../components/AppToolbar';
import { type IslandStep } from '../../components/StepIsland/StepIsland';
import { TopbarStepper } from '../../components/StepIsland/TopbarStepper';
import { StepHint } from '../../components/StepHint/StepHint';
import { fmtLocalDateTime } from '../../utils/datetime';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface ValidationListRow {
  jobId:             string;
  jobOid:            string;
  client:            string;
  projectId:         string;
  projectName:       string;
  sampleMatrix:      string;
  status:            string;
  statusId:          string;
  samplingStatusInt: number;
}

interface StationRow {
  sampleLoginOid: string;
  stationId:      string;
  stationName:    string;
  blended:        boolean;
  latitude:       string | null;
  longitude:      string | null;
  temperature:    string | null;
  humidity:       string | null;
  stationComment: string | null;
}

interface SamplingRow {
  sampleLoginOid: string;
  stationId:      string;
  stationName:    string;
  sampleType:     string;
  sampleId:       string;
  collectorOid:   string | null;
  collectorName:  string | null;
  collectedDate:  string | null;
  sampleComment:  string;
  testSummary:    string;
  enteredBy:      string;
  enteredDate:    string | null;
}

interface MonitoringRow {
  sampleParameterOid: string;
  sampleLoginOid:     string;
  sampleId:           string;
  test:               string;
  methodName:         string;
  parameter:          string;
  result:             string;
  unit:               string;
  monitoredBy:        string;
  monitoredDate:      string | null;
  enteredBy:          string;
  enteredDate:        string | null;
}

type TabKey = 'stations' | 'sampling' | 'monitoring';

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
export default function FieldDataValidationPage() {
  // ── List state ──────────────────────────────────────────────────────────────
  const [listRows,    setListRows]    = useState<ValidationListRow[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listLoadMs,  setListLoadMs]  = useState<number | null>(null);
  const [listError,   setListError]   = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());

  // ── Detail state ────────────────────────────────────────────────────────────
  const [view,          setView]          = useState<'list' | 'detail'>('list');
  const [selectedJob,   setSelectedJob]   = useState<ValidationListRow | null>(null);
  const [activeTab,     setActiveTab]     = useState<TabKey>('stations');
  const [visitedTabs,   setVisitedTabs]   = useState<Set<TabKey>>(new Set());
  const [stations,      setStations]      = useState<StationRow[]>([]);
  const [sampling,      setSampling]      = useState<SamplingRow[]>([]);
  const [monitoring,    setMonitoring]    = useState<MonitoringRow[]>([]);
  // Grid-visible (filtered + sorted) rows per grid — Export uses ONLY these so a
  // column-filtered grid exports just what's shown (user request 2026-08-31).
  const [listVisible,       setListVisible]       = useState<ValidationListRow[]>([]);
  const [stationVisible,    setStationVisible]    = useState<StationRow[]>([]);
  const [samplingVisible,   setSamplingVisible]   = useState<SamplingRow[]>([]);
  const [monitoringVisible, setMonitoringVisible] = useState<MonitoringRow[]>([]);

  // Row-selection checkboxes on all three review tabs — GoLive parity (the
  // FieldDataReview views carry the same leftmost tick column + select-all header).
  // Selection is a REVIEW AID only: Validate/Rollback still act on the whole job
  // (GoLive RollBack_Accepting), so nothing here narrows what those buttons do.
  const [selStationIds,    setSelStationIds]    = useState<Set<string | number>>(new Set());
  const [selSamplingIds,   setSelSamplingIds]   = useState<Set<string | number>>(new Set());
  const [selMonitoringIds, setSelMonitoringIds] = useState<Set<string | number>>(new Set());
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailLoadMs,  setDetailLoadMs]  = useState<number | null>(null);
  // Permission gate ported from GoLive FieldDataReviewController.OnActivated —
  // Validate/Rollback are enabled only for admins or roles with Write=true on
  // the FieldDataReview1 navigation permission.
  const [canWrite, setCanWrite] = useState(true);

  const notify                              = useNotify();
  const { confirm: confirmDlg, prompt: promptDlg } = useConfirm();
  const detailT0 = useRef<number>(0);

  // ── Load list ──────────────────────────────────────────────────────────────
  const loadList = useCallback(async (searchText?: string) => {
    setListLoading(true);
    setListError(null);
    const t0 = performance.now();
    try {
      const params: Record<string, string> = {};
      if (searchText) params.search = searchText;
      const res = await api.get('/api/v1/field-data-validation', { params });
      setListRows((res.data as { data: ValidationListRow[] }).data ?? []);
      setSelectedIds(new Set());
    } catch (e: unknown) {
      setListError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setListLoadMs(performance.now() - t0);
      setListLoading(false);
    }
  }, []);

  useEffect(() => { void loadList(); }, [loadList]);

  // ── Single-select (radio-button style) — only one job may be ticked at a
  // time, since Load/Rollback operate on one record here. Header "select all"
  // collapses to the last row rather than checking every row.
  const handleSelectionChange = useCallback((next: Set<string | number>) => {
    if (next.size <= 1) { setSelectedIds(next); return; }
    const newlyAdded = [...next].find(id => !selectedIds.has(id));
    setSelectedIds(new Set([newlyAdded ?? [...next][next.size - 1]!]));
  }, [selectedIds]);

  // ── Permission gate (ported from GoLive FieldDataReviewController.OnActivated) ──
  useEffect(() => {
    api.get('/api/v1/field-data-validation/permissions')
      .then(res => setCanWrite(Boolean((res.data as { data: { canWrite: boolean } }).data?.canWrite)))
      .catch(() => setCanWrite(false));
  }, []);

  // ── Load detail (after clicking Load) ──────────────────────────────────────
  const openDetail = useCallback(async (job: ValidationListRow) => {
    setSelectedJob(job);
    setDetailLoading(true);
    setActiveTab('stations');
    setVisitedTabs(new Set(['stations']));
    // Clear per-tab ticks so one job's selection never carries into the next.
    setSelStationIds(new Set()); setSelSamplingIds(new Set()); setSelMonitoringIds(new Set());
    detailT0.current = performance.now();
    try {
      const [st, sp, mn] = await Promise.all([
        api.get(`/api/v1/field-data-validation/${job.jobOid}/stations`),
        api.get(`/api/v1/field-data-validation/${job.jobOid}/sampling`),
        api.get(`/api/v1/field-data-validation/${job.jobOid}/monitoring`),
      ]);
      setStations((st.data as { data: StationRow[] }).data ?? []);
      setSampling((sp.data as { data: SamplingRow[] }).data ?? []);
      setMonitoring((mn.data as { data: MonitoringRow[] }).data ?? []);
      setDetailLoadMs(performance.now() - detailT0.current);
      setView('detail');
    } catch {
      notify.error('Failed to load job detail. Please try again.');
    } finally {
      setDetailLoading(false);
    }
  }, [notify]);

  const closeDetail = useCallback(() => {
    setView('list');
    setSelectedJob(null);
    setStations([]); setSampling([]); setMonitoring([]);
    setSelStationIds(new Set()); setSelSamplingIds(new Set()); setSelMonitoringIds(new Set());
    setVisitedTabs(new Set());
    void loadList();
  }, [loadList]);

  // ── Tab navigation — marks tab as visited ───────────────────────────────────
  const switchTab = useCallback((tab: TabKey) => {
    setActiveTab(tab);
    setVisitedTabs(prev => new Set([...prev, tab]));
  }, []);

  const allTabsVisited = visitedTabs.has('stations') && visitedTabs.has('sampling') && visitedTabs.has('monitoring');

  // ── Validate ────────────────────────────────────────────────────────────────
  // Validates ONLY the samples TICKED in the Sampling Information tab, matching
  // GoLive FieldDataReviewController.FieldDataValidated_Execute: it gates on
  //   ((ListView)FindItem("SamplingInformation").InnerView).SelectedObjects.Count > 0
  // ("Select the sampleID to validate" otherwise) and loops
  // `foreach (SampleLogIn objSampling in ...SelectedObjects)`.
  // FIXED 2026-08-03 alongside Rollback (same defect, same form): this posted an
  // EMPTY body, so the server fell back to "all PendingValidation rows for the job"
  // and validated everything regardless of the ticks. The API already accepted
  // selectedSampleOids — only the frontend was omitting them.
  const handleValidate = useCallback(async () => {
    if (!selectedJob) return;
    // NOTE: there is deliberately NO "all three tabs visited" gate (removed
    // 2026-08-03). GoLive FieldDataValidated_Execute gates ONLY on the Sampling
    // selection + the FieldDataReview1 Write permission — it never requires the
    // reviewer to open every tab. The old rule blocked users who legitimately HAD
    // Write (verified: C16294 / lisa@strikewater.com resolves Write=1 on
    // FieldDataReview1), which read as a broken permission. The tab ticks remain as
    // PROGRESS indicators in the stepper/hint, not as a gate.
    if (!canWrite) {
      notify.error('You do not have permission to validate field data.');
      return;
    }
    // GoLive gate — validation is per-sample and needs an explicit pick.
    const picked = sampling
      .filter(r => selSamplingIds.has(r.sampleLoginOid))
      .map(r => r.sampleLoginOid);
    if (picked.length === 0) {
      notify.error('Select the sampleID to validate.');
      setActiveTab('sampling');
      return;
    }
    const ok = await confirmDlg({
      message: `Do you want to Validate ${picked.length} selected sample${picked.length > 1 ? 's' : ''} in job ${selectedJob.jobId}?`,
      tone: 'default',
    });
    if (!ok) return;
    try {
      await runLoading(
        api.post(`/api/v1/field-data-validation/${selectedJob.jobOid}/validate`,
          { selectedSampleOids: picked }),
        { blocking: true, label: 'Validating…' }
      );
      notify.success('Validated successfully.');
      closeDetail();
    } catch (err: unknown) {
      const raw = (err as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
      const msg = typeof raw === 'string' ? raw : (raw as { message?: string } | undefined)?.message ?? 'Validate failed.';
      notify.error(msg);
    }
  }, [selectedJob, allTabsVisited, canWrite, sampling, selSamplingIds, confirmDlg, notify, closeDetail]);

  // ── Rollback (detail view) — rolls back ONLY the samples TICKED in the
  // Sampling Information tab, matching GoLive
  // FieldDataReviewController.FieldDataRollBack_Execute + RollBack_Accepting:
  // the detail-view branch gates on
  //   ((ListView)FindItem("SamplingInformation").InnerView).SelectedObjects.Count > 0
  // ("Select atleast one Sample to rollback" otherwise) and then loops
  // `foreach (SampleLogIn objSampling in lvSamplingInfo.SelectedObjects)`.
  // FIXED 2026-08-03 (user report): this previously sent EVERY row in the tab
  // (`sampling.map(...)`), so ticking 2 samples rolled back the whole job. The
  // API + AI_FieldDataRollback_SP already honoured @SelectedSampleOidsJson — only
  // the frontend was passing the wrong set.
  const handleRollback = useCallback(async () => {
    if (!selectedJob) return;
    // GoLive gate — a rollback is per-sample and needs an explicit pick.
    if (selSamplingIds.size === 0) {
      notify.error('Select atleast one Sample to rollback.');
      setActiveTab('sampling');
      return;
    }
    const picked = sampling
      .filter(r => selSamplingIds.has(r.sampleLoginOid))
      .map(r => r.sampleLoginOid);
    if (picked.length === 0) {
      notify.error('Select atleast one Sample to rollback.');
      setActiveTab('sampling');
      return;
    }
    const count = picked.length;
    const reason = await promptDlg({
      title: 'Rollback Reason',
      message: `Enter reason for rolling back ${count} selected sample${count > 1 ? 's' : ''} in job ${selectedJob.jobId}:`,
      required: true,
    });
    if (!reason) return;
    try {
      await runLoading(
        api.post(`/api/v1/field-data-validation/${selectedJob.jobOid}/rollback`, {
          reason, selectedSampleOids: picked,
        }),
        { blocking: true, label: 'Rolling back…' }
      );
      notify.success('Rolled back to Pending Submission (data intact).');
      closeDetail();
    } catch (err: unknown) {
      const raw = (err as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
      const msg = typeof raw === 'string' ? raw : (raw as { message?: string } | undefined)?.message ?? 'Rollback failed.';
      notify.error(msg);
    }
  }, [selectedJob, sampling, selSamplingIds, promptDlg, notify, closeDetail]);

  // ── Island stepper ──────────────────────────────────────────────────────────
  const islandSteps = useMemo((): IslandStep[] => {
    if (view === 'list') {
      const jobDone = selectedIds.size > 0;
      return [
        { id: 'job',        label: 'Select Job', state: jobDone ? 'done'   : 'active' },
        { id: 'stations',   label: 'Stations',   state: 'todo' },
        { id: 'sampling',   label: 'Sampling',   state: 'todo' },
        { id: 'monitoring', label: 'Monitoring', state: 'todo' },
        { id: 'validate',   label: 'Validate',   state: 'todo' },
      ];
    }
    return [
      { id: 'job',        label: 'Select Job', state: 'done' },
      { id: 'stations',   label: 'Stations',   state: visitedTabs.has('stations')   ? 'done' : 'active' },
      { id: 'sampling',   label: 'Sampling',   state: visitedTabs.has('sampling')   ? 'done' : visitedTabs.has('stations')   ? 'active' : 'todo' },
      { id: 'monitoring', label: 'Monitoring', state: visitedTabs.has('monitoring') ? 'done' : visitedTabs.has('sampling')   ? 'active' : 'todo' },
      { id: 'validate',   label: 'Validate',   state: allTabsVisited ? 'active' : 'todo' },
    ];
  }, [view, selectedIds, visitedTabs, allTabsVisited]);

  // ── List grid columns ───────────────────────────────────────────────────────
  const listCols: AppColumn<ValidationListRow>[] = [
    { field: 'jobId',        header: 'Job ID',       width: 130 },
    { field: 'client',       header: 'Client',       width: 220 },
    { field: 'projectId',    header: 'Project ID',   width: 130 },
    { field: 'projectName',  header: 'Project Name', width: 230 },
    { field: 'sampleMatrix', header: 'Sample Matrix',width: 140 },
    { field: 'status',       header: 'Status',       width: 210,
      render: r => <span className="bl-pill info">{r.status}</span> },
    { field: 'statusId',     header: 'Status ID',    width: 100 },
  ];

  // ── Station grid columns (read-only) ────────────────────────────────────────
  const stationCols: AppColumn<StationRow>[] = [
    { field: 'stationId',      header: 'JobID',             width: 120 },
    { field: 'stationName',    header: 'StationLocation',   width: 160 },
    { field: 'blended',        header: 'Blended',           width: 90, align: 'center',
      render: r => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <input type="checkbox" checked={r.blended} readOnly
            style={{ cursor: 'default', width: 14, height: 14 }} />
        </div>
      ) },
    { field: 'latitude',       header: 'Latitude',          width: 120 },
    { field: 'longitude',      header: 'Longitude',         width: 120 },
    { field: 'temperature',    header: 'Temperature (°F)',  width: 140 },
    { field: 'humidity',       header: 'Humidity (%)',      width: 120 },
    { field: 'stationComment', header: 'StationComment',    width: 200 },
  ];

  // ── Sampling grid columns (read-only) ───────────────────────────────────────
  const samplingCols: AppColumn<SamplingRow>[] = [
    { field: 'stationName',     header: 'StationLocation',  width: 150 },
    { field: 'sampleId',        header: 'SampleID',         width: 140 },
    { field: 'collectorName',   header: 'CollectedBy',      width: 140 },
    { field: 'collectedDate',   header: 'CollectedDate',    width: 160, type: 'datetime',
      render: r => <span>{r.collectedDate ? fmtLocalDateTime(r.collectedDate) : ''}</span> },
    { field: 'testSummary',     header: 'TestSummary',      width: 340 },
    { field: 'sampleComment',   header: 'SampleComment',    width: 180 },
    { field: 'enteredBy',       header: 'EnteredBy',        width: 120 },
    { field: 'enteredDate',     header: 'EnteredDate',      width: 160, type: 'datetime',
      render: r => <span>{r.enteredDate ? fmtLocalDateTime(r.enteredDate) : ''}</span> },
  ];

  // ── Monitoring grid columns (read-only) ─────────────────────────────────────
  const monitoringCols: AppColumn<MonitoringRow>[] = [
    { field: 'sampleId',    header: 'SampleID',       width: 140 },
    { field: 'test',        header: 'Test',           width: 180 },
    { field: 'methodName',  header: 'MethodName',     width: 140 },
    { field: 'parameter',   header: 'Parameter',      width: 180 },
    { field: 'result',      header: 'Result',         width: 90, align: 'right' },
    { field: 'unit',        header: 'Units',          width: 110 },
    { field: 'monitoredBy', header: 'MonitoredBy',    width: 130 },
    { field: 'monitoredDate', header: 'MonitoredDate', width: 160, type: 'datetime',
      render: r => <span>{r.monitoredDate ? fmtLocalDateTime(r.monitoredDate) : ''}</span> },
    { field: 'enteredBy',   header: 'EnteredBy',      width: 120 },
    { field: 'enteredDate', header: 'EnteredDate',    width: 160, type: 'datetime',
      render: r => <span>{r.enteredDate ? fmtLocalDateTime(r.enteredDate) : ''}</span> },
  ];

  const loadedJob = selectedIds.size === 1
    ? listRows.find(r => selectedIds.has(r.jobOid)) ?? null
    : null;

  // ═══════════════════════════════════════════════════════════════════
  // LIST VIEW
  // ═══════════════════════════════════════════════════════════════════
  if (view === 'list') {
    return (
      <div className="bl-page">
        <TopbarStepper steps={islandSteps} />

        <div className="bl-top">
          <div className="dp-page-hdr">
            <div className="dp-breadcrumb">
              <span>Home</span><i className="ti ti-chevron-right" />
              <span>Sampling Management</span><i className="ti ti-chevron-right" />
              <span className="dp-bc-active">Field Data Validation</span>
            </div>

            <div className="dp-title-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <FormTitle pageId="fielddata-validation" auditFormName="Field Data Validation"
                loadMs={listLoadMs} loading={listLoading} />
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  className="ca-btn ca-btn-solid"
                  disabled={!loadedJob}
                  onClick={() => { if (loadedJob) void openDetail(loadedJob); }}
                  title={!loadedJob ? 'Tick a Job ID to load' : `Load ${loadedJob.jobId}`}
                >
                  <span aria-hidden="true">⬇️</span>&nbsp;Load
                </button>
                {/* LIST-view rollback = WHOLE JOB, matching GoLive
                    RollBackListView_Accepting (it loops every SampleLogIn of the
                    selected Samplecheckin). This is intentionally different from the
                    DETAIL-view Rollback, which is scoped to the ticked samples
                    (RollBack_Accepting). Labelled "Rollback Job" and the prompt says so
                    outright, because two buttons both reading "Rollback" with different
                    scopes is what made a whole-job rollback look like a bug
                    (user report 2026-08-03). */}
                <button
                  className="ca-btn"
                  disabled={!loadedJob || !canWrite}
                  title={!canWrite
                    ? 'You do not have permission to roll back field data'
                    : 'Rolls back EVERY sample in the job. To roll back only some samples, click Load and use Rollback there.'}
                  onClick={async () => {
                    if (!loadedJob) return;
                    const reason = await promptDlg({
                      title: 'Rollback Whole Job',
                      message: `This rolls back ALL samples in job ${loadedJob.jobId}. `
                             + `To roll back only some samples, cancel, click Load, tick them on the `
                             + `Sampling Information tab and use Rollback there.\n\n`
                             + `Enter reason for rolling back the whole job:`,
                      required: true,
                    });
                    if (!reason) return;
                    try {
                      await runLoading(
                        api.post(`/api/v1/field-data-validation/${loadedJob.jobOid}/rollback`, { reason }),
                        { blocking: true, label: 'Rolling back…' }
                      );
                      notify.success('Rolled back to Pending Submission (data intact).');
                      void loadList();
                    } catch {
                      notify.error('Rollback failed.');
                    }
                  }}
                >
                  <span aria-hidden="true">↩️</span>&nbsp;Rollback Job
                </button>
              </div>
            </div>

            {/* ONE standard hint (was a redundant .dp-subtitle + StepHint saying the
                same thing — user request 2026-08-31: clean standalone design). */}
            <StepHint tone="guidance">
              <b>Review · Validate (FD-2-3).</b> Tick a Job ID and click <b>Load</b> to review the three tabs, then tick samples to <b>Validate</b> — or <b>Rollback</b> to return them to Pending Submission (data intact). <b>Rollback Job</b> rolls back <em>every</em> sample in the job.
            </StepHint>
          </div>
        </div>

        <div className="bl-grid-area">
          <div style={{ margin: '0 0 8px' }}>
            <AppToolbar
              onRefresh={() => void loadList()}
              refreshing={listLoading}
              exportData={{ filename: 'field-data-validation', ...gridExportMatrix(listVisible, listCols) }}
            />
          </div>

          <div className="bl-card bl-card-flex">
            {listError ? (
              <div className="bl-err" style={{ margin: 12 }}>
                <i className="ti ti-alert-circle" /> {listError}
                <button onClick={() => void loadList()}
                  style={{ marginLeft: 10, color: 'var(--brand-accent,#e07b39)', border: 'none', background: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                  Retry
                </button>
              </div>
            ) : (
              <AppDataGrid<ValidationListRow>
                rows={listRows}
                columns={listCols}
                getRowId={r => r.jobOid}
                loading={listLoading}
                selectable
                selectedIds={selectedIds}
                onSelectionChange={handleSelectionChange}
                onVisibleRowsChange={setListVisible}
                emptyText="Nothing pending validation — submit a job from Field Data Entry."
                onRowDoubleClick={r => void openDetail(r)}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  // DETAIL VIEW
  // ═══════════════════════════════════════════════════════════════════
  const hintContent = allTabsVisited
    ? (<>All three tabs reviewed — tick the samples on <strong>Sampling Information</strong>, then click <strong>Validate</strong> to move them forward, or <strong>Rollback</strong> to send them back.</>)
    : (<><b>Review · Validate.</b> Read-only review of the submitted field data — visit all three tabs, tick the samples on <strong>Sampling Information</strong>, then <strong>Validate</strong>. Rollback returns the selected samples to the previous queue.</>);

  return (
    <div className="bl-page">
      <TopbarStepper steps={islandSteps} />

      <div className="bl-top">
        <div className="dp-page-hdr">
          {/* Full module path (Home > Sampling Management > … ) matching Field Data
              Entry — user request 2026-08-31. */}
          <div className="dp-breadcrumb">
            <span>Home</span>
            <i className="ti ti-chevron-right" />
            <span>Sampling Management</span>
            <i className="ti ti-chevron-right" />
            <span
              style={{ color: 'var(--brand-accent,#e07b39)', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={closeDetail}
            >Field Data Validation</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">{selectedJob?.jobId}</span>
          </div>

          <div className="dp-title-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <FormTitle
              pageId="fielddata-validation"
              title={`Field Data Validation — ${selectedJob?.jobId ?? ''}`}
              auditFormName="Field Data Validation"
              loadMs={detailLoadMs}
              loading={detailLoading}
            />
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                className="ca-btn ca-btn-solid"
                /* Enabled purely by the MAC Write permission (GoLive parity) — the
                   tab-visit rule was removed 2026-08-03, see handleValidate. */
                disabled={!canWrite}
                title={!canWrite
                  ? 'You do not have permission to validate field data'
                  : selSamplingIds.size === 0
                    ? 'Tick the samples to validate on the Sampling Information tab'
                    : `Validate ${selSamplingIds.size} selected sample(s)`}
                onClick={() => void handleValidate()}
              >
                <span aria-hidden="true">✓</span>&nbsp;Validate
              </button>
              <button
                className="ca-btn"
                disabled={!canWrite || sampling.length === 0}
                title={!canWrite
                  ? 'You do not have permission to roll back field data'
                  : sampling.length === 0
                    ? 'No samples to rollback'
                    : selSamplingIds.size === 0
                      // GoLive keeps the action clickable and answers with
                      // "Select atleast one Sample to rollback", so leave it enabled.
                      ? 'Tick the samples to roll back on the Sampling Information tab'
                      : `Roll back ${selSamplingIds.size} selected sample(s)`}
                onClick={() => void handleRollback()}
              >
                <span aria-hidden="true">↩️</span>&nbsp;Rollback
              </button>
            </div>
          </div>

          {/* Always the orange 'guidance' tone — never turn green when all tabs are
              reviewed (user request 2026-08-31). */}
          <StepHint tone="guidance">
            {hintContent}
          </StepHint>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="bl-grid-area">
        <div className="sa-filter-row" style={{ marginBottom: 8 }}>
          {(['stations', 'sampling', 'monitoring'] as TabKey[]).map(tab => {
            const labels: Record<TabKey, string> = {
              stations:   'Station Information',
              sampling:   'Sampling Information',
              monitoring: 'Monitoring Records',
            };
            const visited = visitedTabs.has(tab);
            return (
              <button
                key={tab}
                className={`sa-filter-btn${activeTab === tab ? ' active' : ''}`}
                onClick={() => switchTab(tab)}
              >
                {labels[tab]}
                {visited && <span style={{ color: '#22c55e', marginLeft: 5, fontWeight: 700 }}>✓</span>}
              </button>
            );
          })}
        </div>

        {/* Read-only notice + toolbar */}
        <div style={{ margin: '8px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
          <AppToolbar
            onRefresh={() => {
              if (selectedJob) void openDetail(selectedJob);
            }}
            refreshing={detailLoading}
            exportData={{
              filename: `field-data-validation-${activeTab}`,
              ...(activeTab === 'stations'  ? gridExportMatrix(stationVisible,    stationCols)
                : activeTab === 'sampling'  ? gridExportMatrix(samplingVisible,   samplingCols)
                :                             gridExportMatrix(monitoringVisible, monitoringCols)),
            }}
          />
          <span style={{ fontSize: 11, color: 'var(--ai-muted)', marginLeft: 4 }}>Read-only review.</span>
        </div>

        {/* ── Station Information tab ── */}
        {activeTab === 'stations' && (
          <div className="bl-card bl-card-flex">
            <AppDataGrid<StationRow>
              rows={stations}
              columns={stationCols}
              getRowId={r => r.sampleLoginOid}
              loading={detailLoading}
              emptyText="No station rows."
              selectable
              selectedIds={selStationIds}
              onSelectionChange={setSelStationIds}
              onVisibleRowsChange={setStationVisible}
            />
          </div>
        )}

        {/* ── Sampling Information tab (read-only) — Rollback acts on every row
               shown here (all PendingValidation samples for this job), matching
               GoLive RollBack_Accepting exactly, with no pick step needed ── */}
        {activeTab === 'sampling' && (
          <div className="bl-card bl-card-flex">
            <AppDataGrid<SamplingRow>
              rows={sampling}
              columns={samplingCols}
              getRowId={r => r.sampleLoginOid}
              loading={detailLoading}
              emptyText="No sampling rows."
              selectable
              selectedIds={selSamplingIds}
              onSelectionChange={setSelSamplingIds}
              onVisibleRowsChange={setSamplingVisible}
            />
          </div>
        )}

        {/* ── Monitoring Records tab ── */}
        {activeTab === 'monitoring' && (
          <div className="bl-card bl-card-flex">
            <AppDataGrid<MonitoringRow>
              rows={monitoring}
              columns={monitoringCols}
              getRowId={r => r.sampleParameterOid}
              loading={detailLoading}
              emptyText="No monitoring rows."
              selectable
              selectedIds={selMonitoringIds}
              onSelectionChange={setSelMonitoringIds}
              onVisibleRowsChange={setMonitoringVisible}
            />
          </div>
        )}
      </div>
    </div>
  );
}
